/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import { fromNullable, none, Option } from 'fp-ts/lib/Option';

import { getContext, lineStyle } from '../../map/style';
import { DIV, SPAN, IMG, NodeOrOptional } from '../elements';
import { fromRecord } from '../../locale';
import {
    Inspire,
    ILayerInfo,
    LineStyleConfig,
    LineStyleConfigSimple,
    LineStyleConfigDiscrete,
    LineStyleConfigContinuous,
} from '../../source';
import {
    applyResolutionStyle,
    renderSimpleItemLabel,
    LegendActions,
    divWithOpacityVisibilityClass,
} from '.';
import { nonEmpty } from '../../util';

const logger = debug('sdi:legend-linestring');

const lineGeometry = new LineString([
    [0, 15],
    [32, 15],
]);

const item = (
    geomType: string,
    canvas: HTMLCanvasElement,
    label: string,
    actions: NodeOrOptional,
) =>
    DIV(
        `legend-item ${geomType}`,
        actions,
        DIV(
            'item-style',
            IMG({
                alt: 'linestring image',
                ref: (img: HTMLImageElement | null) => {
                    fromNullable(img).map(img => {
                        canvas.toBlob(blob =>
                            fromNullable(blob).map(blob =>
                                img.setAttribute(
                                    'src',
                                    URL.createObjectURL(blob),
                                ),
                            ),
                        );
                    });
                },
            }),
        ),
        DIV('item-label', SPAN({}, label)),
    );

const renderSimple = (
    config: LineStyleConfigSimple,
    layerInfo: ILayerInfo,
    md: Option<Inspire>,
    actions: LegendActions,
) =>
    fromNullable(getContext(20, 32)).map(ctx => {
        const { canvas, olContext } = ctx;
        const layerAction = fromNullable(actions.layer)
            .map(a => a(layerInfo))
            .getOrElse(none);
        const styles = applyResolutionStyle(
            lineStyle(config),
            new Feature(lineGeometry),
        );
        styles.forEach(style => {
            olContext.setStyle(style);
            olContext.drawGeometry(lineGeometry);
        });
        const label = renderSimpleItemLabel(layerInfo, md);

        return divWithOpacityVisibilityClass(
            layerInfo,
            0,
            item('line', canvas, label, none),
            layerAction,
        );
    });

const renderDiscrete = (
    config: LineStyleConfigDiscrete,
    layerInfo: ILayerInfo,
    _md: Option<Inspire>,
    actions: LegendActions,
) => {
    const styleFn = lineStyle(config);
    const itemAction = (idx: number) =>
        fromNullable(actions.item)
            .map(action => action(layerInfo, idx))
            .getOrElse(none);
    const layerAction = fromNullable(actions.layer)
        .map(action => action(layerInfo))
        .getOrElse(none);

    const items = config.groups.map((group, groupIndex) =>
        nonEmpty(group.values).chain(() =>
            fromNullable(getContext(20, 32)).map(ctx => {
                const { canvas, canvasContext, olContext } = ctx;
                canvasContext.clearRect(0, 0, 20, 32);
                const f = new Feature(lineGeometry);
                f.set(config.propName, group.values[0]);
                const styles = applyResolutionStyle(styleFn, f);
                styles.forEach(style => {
                    olContext.drawFeature(f, style);
                });
                return divWithOpacityVisibilityClass(
                    layerInfo,
                    groupIndex,
                    item(
                        'line',
                        canvas,
                        fromRecord(group.label),
                        itemAction(groupIndex),
                    ),
                );
            }),
        ),
    );

    return DIV('items', layerAction, ...items);
};

const renderContinuous = (
    config: LineStyleConfigContinuous,
    layerInfo: ILayerInfo,
    _md: Option<Inspire>,
    actions: LegendActions,
) => {
    // const { canvas, canvasContext, olContext } = ctx;
    const styleFn = lineStyle(config);
    const itemAction = (idx: number) =>
        fromNullable(actions.item)
            .map(action => action(layerInfo, idx))
            .getOrElse(none);
    const layerAction = fromNullable(actions.layer)
        .map(action => action(layerInfo))
        .getOrElse(none);
    // todo: avoid code repetition, only the Feature label differs from renderDiscrete
    const items = config.intervals.map((interval, idx) =>
        fromNullable(getContext(20, 32)).map(ctx => {
            const { canvas, canvasContext, olContext } = ctx;
            canvasContext.clearRect(0, 0, 20, 32);
            const f = new Feature(lineGeometry);
            const v = interval.low + (interval.high - interval.low) / 2;
            f.set(config.propName, v);
            const styles = applyResolutionStyle(styleFn, f);
            styles.forEach(style => {
                olContext.drawFeature(f, style);
            });
            return divWithOpacityVisibilityClass(
                layerInfo,
                idx,
                item(
                    'line',
                    canvas,
                    fromRecord(interval.label),
                    itemAction(idx),
                ),
            );
        }),
    );

    return DIV('items', layerAction, ...items);
};

const render = (
    config: LineStyleConfig,
    layerInfo: ILayerInfo,
    md: Option<Inspire>,
    actions = {} as LegendActions,
) => {
    // const ctx = getContext(20, 32);

    switch (config.kind) {
        case 'line-simple':
            return renderSimple(config, layerInfo, md, actions);
        case 'line-discrete':
            return renderDiscrete(config, layerInfo, md, actions);
        case 'line-continuous':
            return renderContinuous(config, layerInfo, md, actions);
    }
};

export default render;

logger('loaded');
